import { useTranslation } from "react-i18next";
import { Avatar, Box, Chip, Divider, LinearProgress, Typography } from "@mui/material";

import { User } from "../../../../types/types";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

import { GinoFitScoreEnum } from "../../enum/jobEnums";
import { GinoFitScoreData } from "../../types/candidate";
import { VerificationIcon } from "../../../User/assets/icons/user-info-icons";

const CandidateInfo = ({ ginoFitScoreData }: { ginoFitScoreData: GinoFitScoreData }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateGinoFitScorePage" });
  const { t: tFitScore } = useTranslation("goHire", { keyPrefix: "enum.ginoFitScore" });

  const { fullName, firstTwoChr, role, countryName, nationality, media, club } = useUserDataFormatter(
    // @ts-ignore
    ginoFitScoreData.candidateDetails.user as User
  );

  const fitScoreMatch =
    ginoFitScoreData.candidateDetails.fitScore > 60
      ? GinoFitScoreEnum.strongFit
      : ginoFitScoreData.candidateDetails.fitScore > 30
      ? GinoFitScoreEnum.mediumFit
      : GinoFitScoreEnum.weakFit;

  return (
    <Box sx={styles.candidateDetailsContainer}>
      <Box sx={styles.candidateInfoWrapper}>
        <Box sx={styles.avatarWrapper}>
          <Avatar src={media} sx={styles.avatar}>
            {firstTwoChr}
          </Avatar>
          <VerificationIcon sx={styles.verificationIcon} />
        </Box>
        <Box sx={styles.infoWrapper}>
          <Typography sx={styles.candidateName}>{fullName}</Typography>
          <Typography sx={styles.candidateInfo}>
            {role} {countryName && `• ${countryName}`} {club && `• ${club}`}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={styles.dataContainer}>
        <Box component="fieldset" sx={styles.fieldContainer}>
          <Typography component="label" sx={styles.fieldLabel}>
            {t("jobName")}
          </Typography>
          <Typography sx={styles.jobName}>{ginoFitScoreData.jobName}</Typography>
        </Box>
        <Box component="fieldset" sx={styles.fieldContainer}>
          <Typography component="label" sx={styles.fieldLabel}>
            {t("jobName")}
          </Typography>
          <Box sx={styles.fitScoreContainer}>
            <LinearProgress
              sx={styles.fitScoreProgress(fitScoreMatch)}
              value={ginoFitScoreData.candidateDetails.fitScore}
              variant="determinate"
            />
            <Typography sx={styles.jobName}>{ginoFitScoreData.candidateDetails.fitScore}%</Typography>
            <Chip sx={styles.fitScoreChip(fitScoreMatch)} label={tFitScore(fitScoreMatch)} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CandidateInfo;

const styles = {
  candidateInfoWrapper: {
    display: "flex",
    gap: "24px",
    mb: "12px",
  },
  avatarWrapper: {
    position: "relative",
  },
  avatar: {
    width: "64px",
    height: "64px",
  },
  verificationIcon: {
    position: "absolute",
    bottom: "-12.5px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  infoWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  candidateName: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "32px",
  },
  candidateInfo: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  candidateDetailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "24px",
  },
  dataContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: "12px",
  },
  fieldContainer: {
    border: "none",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    p: 0,
  },
  fieldLabel: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
  jobName: {
    fontFamily: "Montserrat",
    color: "var(--black, #141414)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  fitScoreChip: (fitScoreMatch: GinoFitScoreEnum) => ({
    color:
      fitScoreMatch === GinoFitScoreEnum.strongFit
        ? "var(--Success-700, #067647)"
        : fitScoreMatch === GinoFitScoreEnum.mediumFit
        ? "var(--Yellow---100, #D97706)"
        : "var(--Red---100, #B42318)",

    background:
      fitScoreMatch === GinoFitScoreEnum.strongFit
        ? "var(--Success-50, #ECFDF3)"
        : fitScoreMatch === GinoFitScoreEnum.mediumFit
        ? "var(--Yellow---25, #FEF3C7)"
        : "var(--Red---25, #FEF3C7)",

    borderColor:
      fitScoreMatch === GinoFitScoreEnum.strongFit
        ? "var(--Success-200, #ABEFC6)"
        : fitScoreMatch === GinoFitScoreEnum.mediumFit
        ? "var(--Yellow---500, #DC6803)"
        : "var(--Red---500, #B42318)",

    border: "1px solid",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",

    padding: "2px 8px",
    "& .MuiChip-label": {
      padding: "0",
    },
  }),
  fitScoreContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  fitScoreProgress: (fitScoreMatch: GinoFitScoreEnum) => ({
    width: "90px",
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#E4E7EC",
    "& .MuiLinearProgress-bar": {
      background:
        fitScoreMatch === GinoFitScoreEnum.strongFit
          ? "var(--Success-500, #12B76A)"
          : fitScoreMatch === GinoFitScoreEnum.mediumFit
          ? "var(--Yellow---500, #DC6803)"
          : "var(--Red---500, #B42318)",
      borderRadius: "0",
    },
  }),
};
